import { Link } from "react-router-dom";
import { AlertTriangle, Clock, Lock, Snowflake } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { formatDate } from "@/lib/api";

const FEATURE_LABEL = {
  PORTFOLIO: "Portföy Yönetimi",
  VEHICLES: "Araç Takibi",
  CRM: "Müşteri CRM",
  FINANCE: "Gelir / Gider & Muhasebe",
  REPORTS: "Grafikli Raporlar",
  MAP: "Portföy Haritası",
  SHOOTS: "Çekim Günü",
  DRONE: "Drone Çekim Talebi",
  SCAN: "Belge Tarama",
  CONTRACT: "Satış Sözleşmesi",
  REG_FORM: "Portföy Kayıt Formu",
  DEED: "Tapu Takip",
  CUSTOM_LOGO: "Belgelerde Ofis Logosu",
  PERFORMANCE: "Danışman Performans Raporları",
};

export const DemoBanner = () => {
  const { user, office, subscription } = useAuth();
  if (!office?.is_demo || !subscription || subscription.status === "FROZEN" || subscription.days_left > 3) return null;
  const canPick = ["BROKER", "ACCOUNTANT"].includes(user?.role);
  return (
    <div data-testid="demo-expiry-banner" className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3 rounded-2xl border border-amber-300/80 bg-amber-50/90 px-5 py-3.5">
      <div className="flex items-center gap-3 text-amber-900">
        <Clock className="w-5 h-5 shrink-0" />
        <div className="text-sm">
          <span className="font-semibold">Demo sürenizin bitmesine {subscription.days_left} gün kaldı.</span>{" "}
          {canPick ? "Kesintisiz devam etmek için size uygun paketi seçin." : "Paket seçimi için ofis yöneticinizle iletişime geçin."}
        </div>
      </div>
      {canPick && <Link to="/subscription"><Button size="sm" data-testid="demo-pick-package-btn" className="bg-amber-600 hover:bg-amber-700 text-white">Paket Seç</Button></Link>}
    </div>
  );
};

export const SubscriptionBanner = () => {
  const { user, office, subscription } = useAuth();
  if (!subscription || subscription.status !== "WARNING" || (office?.is_demo && subscription.days_left <= 3)) return null;
  return (
    <div data-testid="subscription-warning-banner" className="mb-6 flex items-center justify-between gap-4 rounded-xl border border-amber-300 bg-amber-50 px-5 py-3">
      <div className="flex items-center gap-3 text-amber-900">
        <AlertTriangle className="w-5 h-5 shrink-0" />
        <div className="text-sm">
          <span className="font-semibold">Aboneliğinizin bitmesine {subscription.days_left} gün kaldı.</span>{" "}
          {formatDate(subscription.period_end)} tarihine kadar ödeme yapılmazsa ofis hesabı dondurulacak.
        </div>
      </div>
      {user?.role === "BROKER" && (
        <Link to="/subscription"><Button size="sm" className="bg-amber-600 hover:bg-amber-700">Aboneliğim</Button></Link>
      )}
    </div>
  );
};

export const FrozenScreen = () => {
  const { user, subscription, logout } = useAuth();
  return (
    <div data-testid="account-frozen-screen" className="max-w-lg mx-auto mt-16 rounded-2xl border border-red-200 bg-white p-10 text-center shadow-sm">
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-red-100 text-red-700 flex items-center justify-center">
        <Snowflake className="w-7 h-7" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Ofis Hesabı Donduruldu</h2>
      <p className="text-slate-600 text-sm mb-1">
        {subscription?.manual_frozen
          ? "Hesabınız sistem yöneticisi tarafından donduruldu."
          : `Abonelik süresi ${formatDate(subscription?.period_end)} tarihinde doldu ve ödeme alınmadı.`}
      </p>
      <p className="text-slate-500 text-sm mb-6">Verileriniz güvende. Ödeme yapıldığında tüm erişim otomatik olarak geri açılır.</p>
      <div className="flex justify-center gap-2">
        {user?.role === "BROKER" && <Link to="/subscription"><Button className="bg-slate-900">Abonelik Bilgileri</Button></Link>}
        <Button variant="outline" onClick={logout}>Çıkış Yap</Button>
      </div>
    </div>
  );
};

export const FeatureGate = ({ feature, children }) => {
  const { hasFeature, subscription, user } = useAuth();
  if (hasFeature(feature)) return children;
  return (
    <div data-testid="feature-locked-screen" className="max-w-lg mx-auto mt-16 rounded-2xl border border-slate-200 bg-white p-10 text-center">
      <div className="w-14 h-14 mx-auto mb-4 rounded-full bg-slate-100 text-slate-700 flex items-center justify-center">
        <Lock className="w-6 h-6" />
      </div>
      <h2 className="text-2xl font-bold text-slate-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>Bu Özellik Kilitli</h2>
      <p className="text-slate-600 text-sm mb-6">
        <span className="font-semibold">{FEATURE_LABEL[feature]}</span> özelliği mevcut{" "}
        <span className="font-semibold">{subscription?.package_name}</span> paketinizde bulunmuyor. Paketi yükselttiğinizde otomatik olarak açılır, mevcut verileriniz korunur.
      </p>
      {user?.role === "BROKER" && (
        <Link to="/subscription"><Button data-testid="upgrade-cta-btn" className="bg-emerald-600 hover:bg-emerald-700">Paketi Yükselt</Button></Link>
      )}
    </div>
  );
};
